<template>
  <div :class="['nav-sidebar', { active: isSidebarExpanded }]" @mouseenter="changeStatus" @mouseleave="changeStatus">
    <header-logo :collapsed-sidebar="isSidebarExpanded" />

    <div :class="['nav-brands', { active: isSidebarExpanded, 'single-brand': singleBrand }]">
      <div class="nav-brands__icon-wrapper">
        <img
          v-if="selectedBrand.icon"
          class="nav-brands__icon"
          :src="selectedBrand.icon"
          :alt="`${selectedBrand.brand}-logo`"
        />
        <span v-else class="nav-brands__icon">{{ getBrandFirstLetter }}</span>
      </div>

      <transition name="fade">
        <el-select-v2
          v-if="isSidebarExpanded && normalizedBrands.length"
          v-model="activeBrandId"
          :options="normalizedBrands"
          :teleported="false"
          filterable
          :disabled="singleBrand"
          popper-class="nav-brands__select"
          @change="onBrandChange"
          @focus="isSelectInFocus = true"
          @blur="isSelectInFocus = false"
        >
          <template #prefix>
            <transition name="slide-fade">
              <span v-if="isSelectInFocus" class="nav-brands__search-icon">
                <i class="icon icon-search-12px"></i>
              </span>
            </transition>
          </template>
          <template #default="{ item }">
            <div>
              <span class="nav-brands__label">{{ item.label }}</span>
            </div>
          </template>
        </el-select-v2>
      </transition>
    </div>

    <div :class="['nav-list', { active: isSidebarExpanded }]">
      <template v-for="items in resolveSidebar" :key="items.id">
        <menu-sidebar-item
          :data-item="items.id"
          :isSidebarExpanded="isSidebarExpanded"
          :color="color"
          :item="items"
          :sub-levels="resolveSubLevels"
          :id-active="idActive"
        />
      </template>

      <menu-sidebar-user-logout :active="isSidebarExpanded" />
    </div>
  </div>
</template>

<script>
import MenuSidebarItem from '@/components/menu/_atoms/MenuSidebarItem.vue';
import HeaderLogo from '@/components/menu/_molecules/HeaderLogo.vue';
import MenuSidebarUserLogout from '@/components/menu/_organisms/SidebarUserLogout.vue';
import bus from '@/helpers/events/bus';
import { routesWithoutChildren } from '@/utilities/constants/constants';
import { isEmpty } from 'lodash';
import { defineComponent } from 'vue';
import { mapActions } from 'vuex';

const OTO_SECTION_ID = '360';
const EXCLUDED_SECTION_ID = 'welcome';

export default defineComponent({
  name: 'SidebarMenu',
  components: {
    HeaderLogo,
    MenuSidebarItem,
    MenuSidebarUserLogout,
  },
  props: {
    brands: {
      type: Array,
      default: null,
    },
    selectedBrand: {
      type: Object,
      default: null,
    },
    menu: {
      type: Array,
      default: null,
    },
    subLevels: {
      type: Array,
      default: null,
    },
    menuItems: {
      type: Array,
      default: null,
    },
    brandRoutes: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      isSidebarExpanded: false,
      idActive: '',
      color: '#888',
      isSelectInFocus: false,
    };
  },
  computed: {
    activeBrandId() {
      return this.selectedBrand?.id || null;
    },
    singleBrand() {
      return this.brands?.length === 1;
    },
    resolveSidebar() {
      const orderedMenu = this.mapOrder(this.menu, this.orderRoutes, 'id');
      if (!orderedMenu.length) return [];

      const filteredMenu = this.filterOtoSection(orderedMenu);
      return filteredMenu.filter(item => this.shouldIncludeMenuItem(item));
    },
    getBrandFirstLetter() {
      return this.selectedBrand?.brand ? this.selectedBrand?.brand[0].toUpperCase() : false;
    },
    /**
     * @description get all routes that has sublevels and return the final routes
     * report has a number as id and doesn't need to search inside menuItems
     */
    resolveSubLevels() {
      return this.subLevels.map(subLevel => ({
        id: subLevel.id,
        routes: this.menuItems.filter(items => subLevel.routes.find(route => route.id === items.id)),
      }));
    },
    /**
     * @description create an array with the correct sort order
     */
    orderRoutes() {
      if (!this.brandRoutes) return;
      return this.brandRoutes?.map(el => el.id);
    },
    normalizedBrands() {
      if (!Array.isArray(this.brands)) return [];
      return this.brands.map(brand => ({
        value: brand.id,
        label: brand.brand,
      }));
    },
  },
  mounted() {
    this.highlightMenu();

    this.triggerVisibleSubcategorie();

    bus.$on('sidebar-brands-fallback', brand => this.selectBrand(brand));
  },
  beforeUnmount() {
    bus.$off('sidebar-brands-fallback');
  },
  methods: {
    ...mapActions(['changeBrand']),
    ...mapActions('base', ['updateSelectedBrand']),

    onBrandChange(brandId) {
      const brand = this.brands.find(brand => brand.id === brandId);
      if (!brand) return;

      this.selectBrand(brand);
    },

    filterOtoSection(orderedMenu) {
      const otoSectionIndex = orderedMenu.findIndex(route => route.id === OTO_SECTION_ID);
      const otoSection = this.resolveSubLevels.find(sublevel => sublevel.id === OTO_SECTION_ID);

      return otoSection && otoSection.routes.length
        ? orderedMenu
        : orderedMenu.filter((_, index) => index !== otoSectionIndex);
    },

    shouldIncludeMenuItem(item) {
      if (item.id === EXCLUDED_SECTION_ID) return false;

      if (routesWithoutChildren.includes(item.id)) return true;

      return this.hasValidRoutes(item.id);
    },

    hasValidRoutes(itemId) {
      const sublevel = this.resolveSubLevels.find(sublevel => sublevel.id === itemId);
      return sublevel?.routes?.length > 0;
    },

    /**
     * @description change sidebar status
     */
    changeStatus(event) {
      const isEntering = event.type === 'mouseenter';

      this.isSidebarExpanded = isEntering;
      if (!isEntering) this.isSelectInFocus = false;

      this.highlightMenu(this.isSidebarExpanded);
      this.triggerVisibleSubcategorie();
    },
    /**
     * @description highlight selected menu option
     */
    highlightMenu(active) {
      this.$nextTick(() => {
        if (!active) {
          const exact =
            document.querySelector('.nav-subcategorie .router-link-exact-active') ||
            document.querySelector('.nav-categorie .router-link-exact-active');
          const navSelected = exact?.closest('.nav-item');
          navSelected?.classList.add('highlight-router');
        } else document.querySelector('.nav-item.highlight-router')?.classList.remove('highlight-router');
      });
    },
    /**
     * @description get id from active sidebar
     */
    resolveSelectedId() {
      this.$nextTick(() => {
        const exact =
          document.querySelector('.nav-subcategorie .router-link-exact-active') ||
          document.querySelector('.nav-categorie .router-link-exact-active');
        this.idActive = exact?.closest('.nav-item').dataset.item;
      });
    },
    /**
     * @description set selected id / emit event to update visible subcategorie
     */
    triggerVisibleSubcategorie() {
      this.resolveSelectedId();
      bus.$emit('sidebar-visible-sublevels');
    },
    /**
     * @description select new brand
     */
    selectBrand(brand) {
      if (isEmpty(brand)) return;

      this.changeBrand(brand);
      bus.$emit('set-baseUrl');

      bus.$emit('dismiss-toasts');
      bus.$emit('update-selected-brand');
      bus.$emit('render-cards', brand);

      this.updateSelectedBrand(true);
    },
    /**
     * @description sort array of objects by another array
     * @param {Array} array - array to be ordered
     * @param {Array} order - array order
     * @param {*} key - key to sort
     */
    mapOrder(array, order, key) {
      array.sort((a, b) => {
        const A = a[key];
        const B = b[key];
        return order.indexOf(A) > order.indexOf(B) ? 1 : -1;
      });
      return array;
    },
  },
});
</script>

<style lang="scss" scoped>
.nav-sidebar {
  background: $blue-700;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.15);
  transition: width 0.6s cubic-bezier(0.25, 1, 0.5, 1);
  height: 100vh;
  overflow: hidden;
  width: 64px;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;

  &.active {
    width: 264px;
  }

  .nav-brands {
    height: 52px;
    padding: 0 9px 0 19px;
    transition: all 0.2s ease-in-out;
    display: flex;
    align-items: center;
    gap: 10px;

    &.active {
      padding: 0 9px 0 12px;
    }

    &__search-icon {
      display: inline-flex;
      align-items: center;
    }

    &__icon {
      background: $pink-400;
      border-radius: 100%;
      color: $color-white;
      font-size: 12px;
      line-height: 20px;
      height: 26px;
      width: 26px;
      display: grid;
      place-content: center;
      cursor: auto;
    }

    &__label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:deep(.el-select__wrapper) {
      box-shadow: none !important;
      border: 1px solid transparent !important;
      border-radius: 30px;
      transition: all 0.2s ease;
      padding: 0 !important;

      &.is-focused {
        border-color: $oto-omni !important;
        box-shadow: none !important;
        padding: 4px 12px !important;
      }

      &.is-filterable {
        cursor: pointer !important;
      }

      &.is-disabled {
        background-color: $color-white;
        cursor: default !important;
      }
    }

    &:deep(.el-select__selected-item) {
      color: $gray-700;
      font-weight: 600;
      font-style: SemiBold;
      font-size: 12px;
      line-height: 20px;
    }
  }

  .nav-list {
    margin-top: 24px;
    padding: 0 8px;
    height: calc(100vh - 160px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: $gray-400 transparent;
    display: flex;
    flex-direction: column;

    & > :last-child {
      margin-top: auto;
    }
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}
</style>

<style lang="scss">
.nav-brands.single-brand .el-select__suffix {
  display: none !important;
}

.nav-brands__select {
  .el-select-dropdown {
    &__item {
      font-size: 12px !important;
      color: $gray-700 !important;
    }
  }
}
</style>
